import { getApiKeysQueryKey, getApiKeysQueryKeyPrefix } from '@kaiizen/kai-pro-api';
import type { ApiCreateApiKey, ApiUpdateApiKey } from '@kaiizen/kai-pro-api';
import { useCachedQuery, useQueryCache } from '@kaiizen/react-persistent-cache';
import { useMemo } from 'react';
import { useKaiProApiClient } from '../useKaiProApiClient';

/** Reads API-key metadata and provides write operations that refresh it after completion. */
export const useApiKeys = () => {
  const kaiProApiClient = useKaiProApiClient();
  const queryCache = useQueryCache();
  const query = useCachedQuery(getApiKeysQueryKey(), () => kaiProApiClient.getApiKeys(), {
    enabled: !!kaiProApiClient.token,
  });

  const apiKeyMutators = useMemo(() => ({
    create: async (payload: ApiCreateApiKey) => {
      const created = await kaiProApiClient.createApiKey(payload);
      await queryCache.refetchAll({ prefix: getApiKeysQueryKeyPrefix() });
      return created;
    },
    update: async (apiKeyId: string, payload: ApiUpdateApiKey) => {
      const updated = await kaiProApiClient.updateApiKey(apiKeyId, payload);
      await queryCache.refetchAll({ prefix: getApiKeysQueryKeyPrefix() });
      return updated;
    },
    revoke: async (apiKeyId: string) => {
      const revoked = await kaiProApiClient.revokeApiKey(apiKeyId);
      await queryCache.refetchAll({ prefix: getApiKeysQueryKeyPrefix() });
      return revoked;
    },
  }), [kaiProApiClient, queryCache]);

  return { ...query, apiKeys: query.data ?? [], apiKeyMutators };
};
